import React, { useState, useEffect } from 'react';
import { API, authHeaders, authHeadersOnly } from '../services/apiConfig';
import './ProductFeedback.css';

function ProductFeedback({ productId, userId, userName, userEmail }) {
  const [reviews, setReviews]     = useState([]);
  const [rating, setRating]       = useState(5);
  const [comment, setComment]     = useState('');
  const [showForm, setShowForm]   = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [avgRating, setAvgRating] = useState(0);

  useEffect(() => { if (productId) fetchReviews(); }, [productId]);

  useEffect(() => {
    const onFocus = () => { if (productId) fetchReviews(); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [productId]);

const fetchReviews = async () => {
    try {
      const res = await fetch(`${API}/api/orders/product-reviews/${productId}`,
        { headers: authHeadersOnly() });
      const data = res.ok ? await res.json() : [];
      const list = Array.isArray(data) ? data.map(r => ({
        id: `r-${r.id}`,
        displayName: (r.userName && r.userName.trim() && r.userName !== 'null') ? r.userName.trim() : 'User',
        rating: r.rating,
        comment: r.reviewText,
        createdAt: r.createdAt,
        verified: r.orderId != null && r.orderId !== 0
      })) : [];
      setReviews(list);
      if (list.length > 0) {
        setAvgRating(list.reduce((s, r) => s + r.rating, 0) / list.length);
      }
    } catch {}
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!userId) { alert('Please login to write a review'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(
        `${API}/api/orders/reviews/write?userId=${userId}`,
        {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({ productId, rating, reviewText: comment })
        }
      );
      if (!res.ok) {
        const msg = await res.text();
        alert(msg || 'Failed to submit review');
        return;
      }
      setComment('');
      setRating(5);
      setShowForm(false);
      fetchReviews();
    } catch {
      alert('Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  const renderStars = (val, size = 18) =>
    [...Array(5)].map((_, i) => (
      <span key={i} style={{ fontSize: size, color: i < Math.round(val) ? '#ffc107' : '#ddd' }}>★</span>
    ));

  return (
    <div className="product-feedback">
      <div className="feedback-header">
        <div>
          <h3>Customer Reviews</h3>
          {reviews.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
              <div>{renderStars(avgRating, 20)}</div>
              <span style={{ fontWeight: '700', fontSize: '16px', color: '#232f3e' }}>
                {avgRating.toFixed(1)}
              </span>
              <span style={{ color: '#888', fontSize: '14px' }}>
                ({reviews.length} review{reviews.length !== 1 ? 's' : ''})
              </span>
            </div>
          )}
        </div>
        {userId && (
          <button className="write-review-btn" onClick={() => setShowForm(!showForm)}>
            ✍️ Write a Review
          </button>
        )}
      </div>

      {showForm && (
        <form className="feedback-form" onSubmit={handleSubmit}>
          <div style={{ marginBottom: '6px', fontWeight: '700', color: '#333', fontSize: '14px' }}>
            Reviewing as: <span style={{ color: '#9A1111' }}>{userName || userEmail || 'You'}</span>
          </div>
          <div className="rating-input">
            <label>Rating:</label>
            <div className="star-rating">
              {[1, 2, 3, 4, 5].map((star) => (
                <span
                  key={star}
                  className={star <= rating ? 'star filled' : 'star'}
                  onClick={() => setRating(star)}
                >★</span>
              ))}
            </div>
          </div>
          <textarea
            placeholder="Share your experience with this product..."
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            required
          />
          <div className="form-actions">
            <button type="submit" className="submit-btn" disabled={submitting}>
              {submitting ? 'Submitting...' : 'Submit Review'}
            </button>
            <button type="button" className="cancel-btn" onClick={() => setShowForm(false)}>Cancel</button>
          </div>
        </form>
      )}

      <div className="feedbacks-list">
        {reviews.length === 0 ? (
          <p className="no-reviews">No reviews yet. Be the first to review!</p>
        ) : (
          reviews.map((review) => (
            <div key={review.id} className="feedback-item">
              <div className="feedback-header-item">
                <div className="user-info">
                  <div className="user-avatar">
                    {review.displayName.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <span className="user-name">{review.displayName}</span>
                  </div>
                </div>
                <div className="feedback-rating">{renderStars(review.rating)}</div>
              </div>
              {review.comment && <p className="feedback-comment">{review.comment}</p>}
              <span className="feedback-date">
                {new Date(review.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
              </span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default ProductFeedback;
